import { generateStatusBackgroundColor } from "@shared/utils/colors";
import React from "react";

import WorkOrderTypeIcons from "../WorkOrderTypeIcons";
import {
  ExternalIcon,
  EXTERNAL_ICON_SIZES,
  NotificationCounter,
  COUNTER_SIZES,
} from "@shared/ui/Indicators";

interface WorkOrderCardIconProps {
  /** Icon name from ticketType */
  iconName?: string;
  /** Icon color (hex) from ticketType */
  color?: string;
  /** Show notification indicator */
  showNotification?: boolean;
  /** Is card hovered */
  isHovered?: boolean;
  /** Is this an external ticket (vs internal work order) - shows external indicator) */
  isExternal?: boolean;
  /** Notification count for the icon */
  notificationCount?: number;
  /** Icon container shape: "circle" (default) or "rounded" (small radius square) */
  shape?: "circle" | "rounded";
  className?: string;
  /** Bubble diameter in px (default 48). Inner glyph + indicators scale with it. */
  size?: number;
}

/**
 * WorkOrderCardIcon
 *
 * Icon component for work order cards with dynamic icon and color support.
 * Follows the same pattern as OEM's RequestRow - uses color with opacity for background,
 * solid color on hover with white icon.
 * Uses WorkOrderTypeIcons component to dynamically render icons based on type.
 */
export const WorkOrderCardIcon = ({
  iconName,
  color = "#0517F8", // Default brand blue
  isExternal = false,
  notificationCount = 0,
  isHovered = false,
  shape = "circle",
  className = "",
  size = 48,
}: WorkOrderCardIconProps): JSX.Element => {
  // Generate background color with opacity (light version) or solid on hover
  const backgroundColor = isHovered
    ? color
    : generateStatusBackgroundColor(color) || `${color}1A`; // Fallback to 10% opacity

  // Icon color: white on hover, solid color otherwise
  const iconColor = isHovered ? "#FFFFFF" : color;

  // Scale the inner glyph and indicators relative to the 48px baseline.
  const isCompact = size <= 36;
  const glyphSize = Math.round((size * 20) / 48);

  // Anchor the counter on the shape's lower-left edge. On a circle the
  // bounding-box corner sits outside the outline, so pull the badge in to the
  // 225° point of the circle (r - r/√2 from each edge) to keep it hugging the
  // icon; a rounded square keeps the corner anchor.
  const counterInset =
    shape === "circle" ? `${(0.5 - 0.5 / Math.SQRT2) * 100}%` : "0";

  return (
    <div
      className={`relative flex-shrink-0 ${
        shape === "rounded" ? "rounded-sm" : "rounded-full"
      } flex items-center justify-center ${className}`}
      style={{ backgroundColor, width: size, height: size }}
    >
      {isExternal && (
        <ExternalIcon
          size={
            isCompact ? EXTERNAL_ICON_SIZES.SMALL : EXTERNAL_ICON_SIZES.MEDIUM
          }
          className="absolute top-0 right-0 translate-x-1/2 -translate-y-1/2"
        />
      )}
      <WorkOrderTypeIcons
        icon={iconName}
        strokeColor={iconColor}
        size={glyphSize}
      />
      {notificationCount > 0 && (
        <div
          className="absolute -translate-x-1/2 translate-y-1/2"
          style={{ bottom: counterInset, left: counterInset }}
        >
          <NotificationCounter
            size={isCompact ? COUNTER_SIZES.MEDIUM : COUNTER_SIZES.LARGE}
            className="border border-solid border-primary"
            count={notificationCount}
            isSizedCounter
          />
        </div>
      )}
    </div>
  );
};
